import { useEffect, useState } from "react";
import axios from "axios";
import Reveal from "@/components/Reveal";
import { ArrowUpRight } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK = "https://images.unsplash.com/photo-1438232992991-995b7058bbb3?q=80&w=1400&auto=format&fit=crop";

const ministries = [
  {
    n: "01",
    caption: "Fuerza Kids",
    name: "Fuerza Kids",
    desc: "Los más pequeños de la casa aprenden de Jesús con juegos, arte y adoración hecha a su medida, mientras tú disfrutas el culto con tranquilidad.",
    slug: "fuerza-kids",
  },
  {
    n: "02",
    caption: "Jóvenes",
    name: "Jóvenes",
    desc: "Una generación que vive su fe sin vergüenza: amigos reales, palabra directa y un propósito más grande que las pantallas.",
    slug: "jovenes",
  },
  {
    n: "03",
    caption: "Alabanza",
    name: "Alabanza",
    desc: "El equipo que abre camino a la presencia de Dios cada reunión, dirigido por la Pastora Milena Sales. Si tocas, cantas o te apasiona la producción, hay lugar para ti.",
    slug: "alabanza",
  },
  {
    n: "04",
    caption: "Servidores",
    name: "Servidores",
    desc: "Las manos que hacen posible cada reunión: bienvenida, logística y amor práctico. Aquí nadie es espectador.",
    slug: "servidores",
  },
];

const waLink = (name) =>
  `https://wa.me/527223947826?text=${encodeURIComponent(`Hola, me interesa unirme al ministerio de ${name} en Viento de Gracia`)}`;

const Ministerios = () => {
  const [photos, setPhotos] = useState({});

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then(({ data }) => {
        const map = {};
        data.forEach((p) => {
          if (!map[p.caption]) map[p.caption] = `${BACKEND_URL}${p.url}`;
        });
        setPhotos(map);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="ministerios" className="border-t border-white/10 bg-ink py-28 lg:py-36" data-testid="ministerios-section">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <div className="mb-16 grid gap-8 lg:grid-cols-2">
          <Reveal>
            <p className="mb-8 flex items-center gap-4 text-[10px] font-light uppercase tracking-[0.5em] text-fire" data-testid="ministerios-eyebrow">
              <span className="inline-block h-px w-16 bg-fire" /> Ministerios
            </p>
            <h2 className="font-serif text-5xl leading-[0.95] tracking-tight text-paper sm:text-7xl" data-testid="ministerios-title">
              Todos tenemos <em className="italic text-gold">un lugar</em>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="flex items-end">
            <p className="max-w-md text-sm font-light leading-relaxed tracking-wide text-smoke">
              La iglesia no es un edificio ni un evento: es una familia donde cada persona sirve, crece y pertenece.
              Conoce nuestros ministerios y súmate.
            </p>
          </Reveal>
        </div>

        <div className="grid border border-white/10 lg:grid-cols-2">
          {ministries.map((m, i) => (
            <Reveal key={m.slug} delay={i * 0.08} className={i > 0 ? "border-t border-white/10 lg:border-t-0 lg:[&:nth-child(even)]:border-l lg:[&:nth-child(n+3)]:border-t" : ""}>
              <article className="group relative" data-testid={`ministerio-card-${m.slug}`}>
                <div className="relative overflow-hidden">
                  <img
                    src={photos[m.caption] || FALLBACK}
                    alt={`Ministerio ${m.name} de Viento de Gracia`}
                    loading="lazy"
                    className="aspect-[16/9] w-full object-cover opacity-80 transition-all duration-[1.2s] ease-out group-hover:scale-105 group-hover:opacity-100"
                    data-testid={`ministerio-img-${m.slug}`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                  <span className="outline-text pointer-events-none absolute right-6 top-4 select-none font-serif text-7xl leading-none">
                    {m.n}
                  </span>
                </div>
                <div className="relative -mt-16 px-8 pb-10 lg:px-10">
                  <h3 className="font-serif text-4xl text-paper transition-colors duration-300 group-hover:text-fire">{m.name}</h3>
                  <p className="mt-3 max-w-md text-sm font-light leading-relaxed tracking-wide text-paper/70">{m.desc}</p>
                  <a
                    href={waLink(m.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`ministerio-join-${m.slug}`}
                    className="mt-5 inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.3em] text-paper/60 transition-colors duration-300 hover:text-fire"
                  >
                    Quiero unirme <ArrowUpRight size={13} weight="regular" />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Ministerios;
